import React, { useMemo, useCallback, useEffect, useContext } from 'react';
import styled, { useTheme, StyleSheetManager } from 'styled-components';
import { Switch, Route } from 'react-router-dom';
import stylisRTLPlugin from 'stylis-plugin-rtl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { browserName } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import { FeatureGateProvider } from '@ubeya/shared/contexts/FeatureGateContext';
import { PermissionGateProvider } from '@ubeya/shared/contexts/PermissionGateContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import useModules from '@ubeya/shared/hooks/account/useModules';
import {
  FlexColumn,
  FlexCenter,
  Loader as BaseLoader,
  ErrorBoundary,
  Toaster,
  ModalProvider
} from '@ubeya/shared-web/components';
import useTranslatedApp from '@ubeya/shared/hooks/useTranslatedApp';
import useInitialFetch from '@ubeya/shared/hooks/account/useInitialFetch';
import { ConfigContextProvider } from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext, { PrivilegesContextProvider } from '@ubeya/shared-web/contexts/PrivilegesContext';
import GlobalStyle from '@ubeya/shared-web/style/global';
import { media } from '@ubeya/shared-web/style';
import joinPrivilegesWithModules from '@ubeya/shared-web/joinPrivilegesWithModules';
import privileges, { mapPrivileges, ubeyaPrivileges, allPrivilegesExcept } from '@ubeya/shared-web/privileges';
import { selectedEmployee, chatCount, helpDeskSidePanel } from '@ubeya/shared/atoms/shared';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { useQueryParams } from '@ubeya/shared-web/hooks/useQueryParams';
import { APP_SIDEBAR_COLLAPSED_WIDTH } from '@ubeya/shared-web/hooks/useAppSidebarExpand';
import { initAmplitude } from '@ubeya/shared-web/analytics/amplitude';
import { convertToMs } from '@ubeya/shared/hooks/auth/inactivityUtils';
import { ACCOUNT_SETTINGS_WITHOUT_EDIT_PERMISSIONS_ORGANIZATION_IDS } from '@ubeya/shared/utils/accounts';
import { AccountRedirect, EmployeeCard } from 'shared/components';
import useInitialState from '../hooks/useInitialState';
import SideBar from './SideBar';
import NewAccountOnboarding from './NewAccountOnboarding';
import ChatBackground from './ChatBackground';
import Confetti from './Confetti';
import Intercom from './Intercom';
import HelpDeskSidePanel from 'helpDesk/components/HelpDeskSidePanel';
import { getDefaultPath } from 'app/utils';
import { getCanAccessAiAgent } from 'aiAgent/aiAgentAccess';
const Dashboard = React.lazy(() => import('dashboard'));
const Employees = React.lazy(() => import('employees'));
const IsHostEmployees = React.lazy(() => import('isHost/components/Employees'));
const Scheduling = React.lazy(() => import('scheduling'));
const Project = React.lazy(() => import('scheduling/components/Project'));
const Employee = React.lazy(() => import('employees/components/Employee'));
const Hiring = React.lazy(() => import('hiring'));
const TimeSheet = React.lazy(() => import('timeSheet'));
// const IsHostTimesheet = React.lazy(() => import('isHost/components/Timesheet'));
const Feed = React.lazy(() => import('feed'));
const IsHostFeed = React.lazy(() => import('isHost/components/Feed'));
const Reports = React.lazy(() => import('reports'));
const InsightsReportsPage = React.lazy(() => import('reports/components/InsightsReportsPage'));
const ReportsHistoryReport = React.lazy(() => import('reports/components/ReportsHistoryReport'));
const ReportHistoryViewReport = React.lazy(() => import('reports/components/ReportHistoryViewReport'));
const IsHostReports = React.lazy(() => import('isHost/components/Reports'));
const Settings = React.lazy(() => import('settings'));
const CRM = React.lazy(() => import('crm'));
const IsHostCRM = React.lazy(() => import('isHost/components/Crm'));
const Payroll = React.lazy(() => import('payroll'));
const IsHostPayroll = React.lazy(() => import('isHost/components/Payroll'));
const Billing = React.lazy(() => import('billing'));
const Orders = React.lazy(() => import('orders'));
const Sequences = React.lazy(() => import('sequences'));
const IsHostSequences = React.lazy(() => import('isHost/components/Sequences'));
const IsHostCompliances = React.lazy(() => import('isHost/components/Compliances'));
const Compliances = React.lazy(() => import('compliances'));
const LiveView = React.lazy(() => import('liveView'));
const Integrations = React.lazy(() => import('integrations'));
const AiInsightsAgent = React.lazy(() => import('aiInsightsAgent'));
const IntegrationsCodeExchange = React.lazy(() => import('integrations/components/IntegrationsCodeExchange'));
const Invoices = React.lazy(() => import('invoices'));
const PayrollProcessHistory = React.lazy(() => import('payrollProcessHistory'));
const HelpDesk = React.lazy(() => import('helpDesk'));

const Wrapper = styled.div`
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  display: grid;
  grid-template-rows: auto 1fr;
  ${media.tablet`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    min-height: 0;
  `}
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: hidden;
  min-width: 0;
  ${media.tablet`
    padding-inline-start: ${({ $collapsedSidebarWidth = 0 }) => $collapsedSidebarWidth}px;
  `}
`;

const LoaderWrapper = styled(FlexCenter)`
  height: calc(var(--vh, 1vh) * 100);
`;

const EmptyWrapper = styled.div``;

const Loader = () => (
  <LoaderWrapper>
    <BaseLoader />
  </LoaderWrapper>
);

const AuthorizedApp = React.memo(({ language, children }) => {
  const isRtl = useTranslatedApp(language);
  const rtlSupport = isRtl ? [stylisRTLPlugin] : undefined;

  const setChatCounter = useSetRecoilState(chatCount);
  const [selectedUser, setSelectedUser] = useRecoilState(selectedEmployee);
  const helpDeskPanel = useRecoilValue(helpDeskSidePanel);
  const myPrivileges = useContext(PrivilegesContext);

  const onClose = useCallback(() => setSelectedUser(null), [setSelectedUser]);
  const { fullScreen } = useQueryParams();

  useInitialFetch();
  const { isLoading: isLoadingInitialState } = useInitialState();

  const { isLoading: isLoadingUser, data: usersData, accounts } = useUser();
  const { isLoading: isLoadingAccount, accountId, account, config } = useAccount();
  const { isLoading: isLoadingModules, isSuccess: areModulesLoaded, modules } = useModules();
  const { isLoading: isLoadingBranches, isSuccess: areBranchesLoaded, branches } = useBranches();

  const currentAccountEntry = useMemo(() => accounts.find(({ id }) => id === accountId), [accounts, accountId]);

  // Raw membership, like the privileges column: branchesOptions reads the elevated context instead.
  const adminBranches = useMemo(() => {
    const privilegeIds = currentAccountEntry?.privileges;

    if (!areBranchesLoaded) {
      return undefined;
    }

    if (privilegeIds?.includes(privileges.ACCOUNT_ADMIN)) {
      return branches;
    }

    return privilegeIds?.includes(privileges.BRANCH_ADMIN) ? branches.filter(({ isAdmin }) => isAdmin) : undefined;
  }, [areBranchesLoaded, branches, currentAccountEntry]);

  // Settled, not necessarily successful: a source that failed is passed as undefined so its column is left alone.
  const allDataLoaded = !isLoadingUser && !isLoadingAccount && !isLoadingModules && !isLoadingBranches && !!accountId;

  if (isLoadingInitialState) {
    return <Loader />;
  }

  const SideBarComponent = fullScreen ? EmptyWrapper : SideBar;
  const contentCollapsedSidebarWidth = fullScreen ? 0 : APP_SIDEBAR_COLLAPSED_WIDTH;

  return (
    <StyleSheetManager stylisPlugins={rtlSupport}>
      <>
        <GlobalStyle isRtl={isRtl} browserName={browserName} />

        <Wrapper>
          <SideBarComponent />
          <Container $collapsedSidebarWidth={contentCollapsedSidebarWidth}>
            <ErrorBoundary accountId={account?.id} adminId={usersData?.id}>
              {children}
            </ErrorBoundary>
          </Container>
          <Toaster />
          {!!myPrivileges[privileges.VIEW_CHAT] && (
            <ChatBackground
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              setChatCounter={setChatCounter}
              accountId={account?.id}
            />
          )}
          {!account?.isHost && modules?.help_desk && helpDeskPanel.isOpen && <HelpDeskSidePanel />}

          {/* {firstLaunch && <FirstLaunch onClose={() => setFirstLaunch(false)} />} */}
          {selectedUser && <EmployeeCard defaultData={selectedUser} onClose={onClose} />}
          <Confetti />

          {allDataLoaded && (
            <Intercom
              data={usersData}
              account={account}
              billingStatus={config.billingStatus}
              accounts={accounts}
              branches={areBranchesLoaded ? branches : undefined}
              adminBranches={adminBranches}
              modules={areModulesLoaded ? modules : undefined}
              privilegeIds={currentAccountEntry?.privileges}
            />
          )}
        </Wrapper>
      </>
    </StyleSheetManager>
  );
});

const AppShell = () => {
  const { data, accounts, isUbeyaAdmin, ubeyaAdminViewAsClient } = useUser();
  const adminId = data?.id;

  const {
    isLoading,
    accountId,
    account: { language, isHost, showOnboarding },
    config
  } = useAccount();

  useEffect(() => {
    const { billingStatus, countryCode } = config || {};

    const accountIds = (accounts || []).map(({ id }) => id);
    initAmplitude({ accountId, accountIds, status: billingStatus, adminId: data?.id, isUbeyaAdmin });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'AdminLoad',
      status: billingStatus,
      countryCode: countryCode
    });
  }, [accountId, accounts, config, data?.id, isUbeyaAdmin]);

  // Read once here rather than in every Avatar/User instance (there can be hundreds on screen) -
  // react-query would dedupe the network call either way, but this avoids per-instance subscriptions
  // and re-render fanout; propagated to the whole app via ConfigContext below.
  const {
    account: { config: { isShowLastNameFirst = false } = {} }
  } = useAccountConfig();

  const { t } = useTranslation();
  const theme = useTheme();
  const { isLoading: modulesLoading, modules } = useModules();

  const { isRestrictedFromEmployees } = accounts.find(({ id }) => id === accountId) || {};

  const privilegesWithModules = useMemo(() => {
    const { UBEYA_ADMIN } = privileges;
    const { privileges: myPrivileges } = accounts.find(({ id }) => id === accountId) || [];

    const mappedPrivileges = isUbeyaAdmin
      ? ubeyaAdminViewAsClient
        ? Object.fromEntries(
            Object.entries(ubeyaPrivileges).map(([key, value]) =>
              key === UBEYA_ADMIN.toString() ? [key, false] : [key, value]
            )
          )
        : ubeyaPrivileges
      : mapPrivileges(myPrivileges);

    return isUbeyaAdmin && !ubeyaAdminViewAsClient
      ? ubeyaPrivileges
      : joinPrivilegesWithModules(mappedPrivileges, modules);
  }, [accountId, accounts, isUbeyaAdmin, ubeyaAdminViewAsClient, modules]);

  const configContext = useMemo(() => ({ ...config, isShowLastNameFirst, t, theme }), [
    config,
    isShowLastNameFirst,
    t,
    theme
  ]);

  const showPremiumComponentModule = useMemo(() => !isUbeyaAdmin && !!isHost, [isHost, isUbeyaAdmin]);

  const showComplianceModule = showPremiumComponentModule || config.isAccountNetworkAccessSupplier;

  const defaultPath = getDefaultPath({ isHost, privilegesWithModules });

  if (isLoading || modulesLoading) {
    return <Loader />;
  }

  const canAccessAiAgent = getCanAccessAiAgent({
    isUbeyaAdmin,
    adminId,
    accountId,
    adminPrivileges: privilegesWithModules,
    billingStatus: config?.billingStatus
  });

  return (
    <ConfigContextProvider config={configContext}>
      <PrivilegesContextProvider privileges={privilegesWithModules}>
        <AuthorizedApp language={language} showOnboarding={showOnboarding}>
          <React.Suspense fallback={<Loader />}>
            <ModalProvider>
              <PermissionGateProvider>
                <FeatureGateProvider>
                  <Switch>
                    <Route path="/:accountId/dashboard" component={Dashboard} />
                    {(!!privilegesWithModules[privileges.BRANCH_ADMIN] ||
                      !!privilegesWithModules[privileges.ACCOUNT_ADMIN]) &&
                      !isRestrictedFromEmployees && (
                        <Route
                          exact
                          path="/:accountId/employees/candidates"
                          render={() => <Employees activeTab="candidates" />}
                        />
                      )}

                    {!isRestrictedFromEmployees && (
                      <Route exact path="/:accountId/employees/:employeeId" component={Employee} />
                    )}

                    {(!!privilegesWithModules[privileges.BRANCH_ADMIN] ||
                      !!privilegesWithModules[privileges.ACCOUNT_ADMIN]) &&
                      !isRestrictedFromEmployees && (
                        <Route
                          path="/:accountId/employees"
                          component={showPremiumComponentModule ? IsHostEmployees : Employees}
                        />
                      )}

                    {privilegesWithModules[privileges.ATS] && !!modules?.ats && !isRestrictedFromEmployees && (
                      <Route path="/:accountId/hiring" component={Hiring} />
                    )}

                    {privilegesWithModules[privileges.SCHEDULING] && (
                      <Route path="/:accountId/scheduling/branch/:branchId/project/:projectId" component={Project} />
                    )}

                    {privilegesWithModules[privileges.SCHEDULING] && (
                      <Route path="/:accountId/scheduling" component={Scheduling} />
                    )}
                    {privilegesWithModules[privileges.TIMESHEET] && (
                      <Route path="/:accountId/live" component={LiveView} />
                    )}

                    {privilegesWithModules[privileges.TIMESHEET] && (
                      <Route path="/:accountId/timesheet" component={TimeSheet} />
                    )}
                    {privilegesWithModules[privileges.FEED] && (
                      <Route path="/:accountId/feed" component={showPremiumComponentModule ? IsHostFeed : Feed} />
                    )}
                    {privilegesWithModules[privileges.REPORTS] && (
                      <Route
                        path="/:accountId/reports"
                        component={showPremiumComponentModule ? IsHostReports : Reports}
                      />
                    )}
                    {privilegesWithModules[privileges.REPORTS] && (
                      <Route path="/:accountId/insights" component={InsightsReportsPage} />
                    )}
                    {privilegesWithModules[privileges.REPORTS] && (
                      <Route path="/:accountId/reports-history/:reportHistoryId" component={ReportHistoryViewReport} />
                    )}
                    {privilegesWithModules[privileges.REPORTS] && (
                      <Route path="/:accountId/reports-history" component={ReportsHistoryReport} />
                    )}

                    {privilegesWithModules[privileges.CRM] && (
                      <Route path="/:accountId/crm" component={showPremiumComponentModule ? IsHostCRM : CRM} />
                    )}

                    {privilegesWithModules[privileges.BILLING] && (
                      <Route path="/:accountId/billing" component={Billing} />
                    )}

                    {privilegesWithModules[privileges.PAYROLL] && (
                      <Route
                        path="/:accountId/payroll"
                        component={showPremiumComponentModule ? IsHostPayroll : Payroll}
                      />
                    )}

                    {privilegesWithModules[privileges.SUPPLIERS] && (
                      <Route path="/:accountId/suppliers" component={Orders} />
                    )}

                    {privilegesWithModules[privileges.SEQUENCE] && (
                      <Route
                        path="/:accountId/sequences"
                        component={showPremiumComponentModule ? IsHostSequences : Sequences}
                      />
                    )}
                    {privilegesWithModules[privileges.SHIFT_TASKS] && (
                      <Route
                        path="/:accountId/timesheet-sequences"
                        component={showPremiumComponentModule ? IsHostSequences : Sequences}
                      />
                    )}
                    {(privilegesWithModules[privileges.COMPLIANCE] || showComplianceModule) && (
                      <Route
                        path="/:accountId/compliances"
                        component={showComplianceModule ? IsHostCompliances : Compliances}
                      />
                    )}

                    {(ACCOUNT_SETTINGS_WITHOUT_EDIT_PERMISSIONS_ORGANIZATION_IDS.includes(config?.organizationId) ||
                      privilegesWithModules[privileges.EDIT_PERMISSIONS]) && (
                      <Route path="/:accountId/settings" component={Settings} />
                    )}

                    {(!!privilegesWithModules[privileges.BRANCH_ADMIN] ||
                      !!privilegesWithModules[privileges.ACCOUNT_ADMIN]) &&
                      (modules.app_store || isUbeyaAdmin) && (
                        <Route path="/:accountId/integrations" component={Integrations} />
                      )}

                    {(!!privilegesWithModules[privileges.BRANCH_ADMIN] ||
                      !!privilegesWithModules[privileges.ACCOUNT_ADMIN]) &&
                      (modules.app_store || isUbeyaAdmin) && (
                        <Route path="/integrations/oauth2" component={IntegrationsCodeExchange} />
                      )}
                    {privilegesWithModules[privileges.INVOICE] && (
                      <Route path="/:accountId/invoices/:page?" component={Invoices} />
                    )}

                    {privilegesWithModules[privileges.EXPORT_PROCESS_PAYROLL] &&
                      privilegesWithModules[privileges.ACCOUNT_ADMIN] &&
                      privilegesWithModules[privileges.PAYROLL] && (
                        <Route path="/:accountId/process-payroll-history" component={PayrollProcessHistory} />
                      )}

                    {canAccessAiAgent && <Route path="/:accountId/ai-insight-agent" component={AiInsightsAgent} />}

                    {modules?.help_desk &&
                      (privilegesWithModules[privileges.BRANCH_ADMIN] ||
                        privilegesWithModules[privileges.ACCOUNT_ADMIN]) && (
                        <Route path="/:accountId/help-desk" component={HelpDesk} />
                      )}

                    <AccountRedirect to={defaultPath} />
                  </Switch>
                </FeatureGateProvider>
              </PermissionGateProvider>
            </ModalProvider>
          </React.Suspense>
        </AuthorizedApp>
      </PrivilegesContextProvider>
    </ConfigContextProvider>
  );
};

const Onboarding = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const configContext = useMemo(() => ({ t, theme }), [t, theme]);

  const privilegesWithModules = allPrivilegesExcept(privileges.UBEYA_ADMIN);
  return (
    <PrivilegesContextProvider privileges={privilegesWithModules}>
      <ConfigContextProvider config={configContext}>
        <AuthorizedApp language="en">
          <NewAccountOnboarding />
        </AuthorizedApp>
      </ConfigContextProvider>
    </PrivilegesContextProvider>
  );
};

const App = () => {
  const { isLoading, isAuth, accounts, startInactivityInterval } = useUser();

  const accountInactivityConfig = useMemo(() => {
    const accountsWithInactivityInternal = accounts?.filter(({ config }) => config?.inactivityInterval > 0);
    // find account with the lowest inactivityInterval
    const chosenAccount =
      accountsWithInactivityInternal?.length > 0 &&
      accountsWithInactivityInternal.reduce(
        (minimumAccount, account) =>
          convertToMs(account.config.inactivityInterval, account.config.inactivityIntervalPeriod) <
          convertToMs(minimumAccount.config.inactivityInterval, minimumAccount.config.inactivityIntervalPeriod)
            ? account
            : minimumAccount,
        accountsWithInactivityInternal[0]
      );
    return {
      accountInactivityInterval: chosenAccount?.config?.inactivityInterval || 0,
      accountInactivityIntervalPeriod: chosenAccount?.config?.inactivityIntervalPeriod || 'minutes'
    };
  }, [accounts]);

  useEffect(() => {
    if (isAuth) {
      const { accountInactivityInterval, accountInactivityIntervalPeriod } = accountInactivityConfig;
      return startInactivityInterval({ accountInactivityInterval, accountInactivityIntervalPeriod });
    }
  }, [accountInactivityConfig, isAuth, startInactivityInterval]);

  if (isLoading || !isAuth) {
    return <Loader />;
  }

  if (accounts.length === 0) {
    return <Onboarding />;
  }

  return (
    <Switch>
      <Route path="/:accountId?" component={AppShell} />
    </Switch>
  );
};

export default App;
